import {Fragment, PropsWithChildren, useEffect, useMemo, useRef, useState} from 'react';
import useDigitInput from 'react-digit-input';
import {PasswordInputType} from '../common/types/components';
import Popup from './shared/Popup';
import {HiEye, HiEyeOff} from "react-icons/hi";
import {Input} from "postcss";
import {checkPassword} from "../utils/checkPassword";
import {Switch} from "@headlessui/react";
import {classNames} from "../utils/presentation";

const MIN_LENGTH = 8;
export default function WalletPasswordInput(
    {
        open,
        onComplete,
        type,
        failedAttempts = 0
    }: {
        open: boolean;
        onComplete: (input: string) => void;
        type: PasswordInputType;
        failedAttempts?: number;
    }) {
    const [value, setValue] = useState("");
    const [confirmationValue, setConfirmationValue] = useState("");
    const [hidden, setHidden] = useState(true);
    const [acknowledged, setAcknowledged] = useState(false);
    const validation = useMemo(() => {
        return checkPassword(value);
    }, [value]);
    const isComplete = useMemo(() => {
        if (type === PasswordInputType.LOGIN) {
            return value.length >= MIN_LENGTH;
        }
        return validation.valid && value === confirmationValue && acknowledged;
    }, [type, validation.valid, value, confirmationValue, acknowledged]);

    const title = type === PasswordInputType.LOGIN ? "Input your inventory Password" : "Set your inventory Password";

    const action = type === PasswordInputType.LOGIN ? "Unlock 🔓" : "Set Password";

    return (
        <Popup
            open={open}
            setOpen={() => {}}
            closeOnOutsideClick={false}
            closeButton={false}
            allowFullScreen={false}
            allignTop={true}
            widthClass="w-full md:w-auto md:max-w-md"
        >
            <div className="flex flex-col justify-center items-center w-full">
                <h3 className="text-lg font-medium leading-6 text-gray-900">{title}</h3>
                <form
                    className="w-full"
                    onSubmit={(event) => {
                        event.preventDefault();
                        if (!isComplete) {
                            return;
                        }
                        onComplete(value);
                    }}
                >
                    <div className="flex items-center justify-between mt-6 w-full px-4">
                        <input
                            name="password"
                            className="border border-primary-darkest bg-secondary-lightest w-full h-10 text-center text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-darkest focus:ring-offset-2"
                            type={hidden ? "password" : "text"}
                            value={value}
                            onChange={(event) => setValue(event.target.value)}
                            autoFocus={true}
                            aria-label="Password"
                            placeholder="Password"
                        />
                    </div>
                    {failedAttempts > 0 && (
                        <p className="text-xs text-red mt-2">
                            Invalid Password. You can try again.
                        </p>
                    )}
                    {type === PasswordInputType.REGISTER && (
                        <p className="mt-3 text-xs text-theme-text-light px-2">
                            Set a strong Password with minimum 8 characters, numbers, lowercase and
                            uppercase letters, and special characters.
                        </p>
                    )}
                    {type === PasswordInputType.REGISTER && (
                        <div className="flex items-center justify-between mt-3 w-full px-4">
                            <input
                                name="confirm-password"
                                className="border border-primary-darkest bg-secondary-lightest w-full h-10 text-center text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-darkest focus:ring-offset-2"
                                type={hidden ? "password" : "text"}
                                value={confirmationValue}
                                onChange={(event) => setConfirmationValue(event.target.value)}
                                aria-label="Confirm Password"
                                placeholder="Confirm Password"
                            />
                        </div>
                    )}
                    {type === PasswordInputType.REGISTER && <AcknowledgeButton
                        value={acknowledged}
                        onChange={setAcknowledged}
                    />}
                    <div className="mt-5 w-full flex items-center justify-center relative">
                        <button
                            disabled={!isComplete}
                            type="submit"
                            className="w-3/4 inline-flex justify-center border border-transparent bg-secondary disabled:bg-gray-400 px-4 py-2 text-base font-medium text-theme-text shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-darkest focus:ring-offset-2 sm:text-sm"
                        >
                            {action}
                        </button>
                        <button
                            className="absolute p-1 right-0 font-normal text-gray-500"
                            onClick={() => setHidden(!hidden)}
                            aria-label={hidden ? "Show PIN" : "Hide PIN"}
                            type="button"
                        >
                            {hidden
                                ? <HiEyeOff className="w-5 h-5" aria-hidden="true"/>
                                : <HiEye className="w-5 h-5" aria-hidden="true"/>
                            }
                        </button>
                    </div>
                </form>
                {type === PasswordInputType.LOGIN && (
                    <p className="max-w-xs text-xs mt-4 text-gray-600">
                        Make sure you keep your Password safe.{" "}
                        <span className="font-bold">If you lose your Password, you lose access to your inventory wallet.</span>
                    </p>
                )}
            </div>
        </Popup>
    );
};

const AcknowledgeButton = ({value, onChange}: {
    value: boolean,
    onChange: (value: boolean) => void
}) => {
    return (
        <div className="flex items-center w-full mt-6 px-4 space-x-4">
            <input
                type="checkbox"
                checked={value}
                onChange={(event) => onChange(event.target.checked)}
                id="acknowledge-description"
                className="w-5 h-5 text-secondary accent-secondary focus:ring-primary-darkest focus:outline-none focus:ring-1 focus:ring-offset-1"
            />
            <label
                htmlFor="acknowledge-description"
                className="mr-4 font-bold text-xs text-left"
            >
                I understand that if I lose my Password, I lose access to my inventory wallet.
            </label>
        </div>
    )
}
